import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import arrowDown from '@/images/ArrowDown.svg';
import { useGetTranslationGroup } from '@/state/translations';
import styled from '@emotion/styled';
import { FormControl, MenuItem, Select, SelectChangeEvent, Theme, useTheme } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useCurrentDraftGroupId } from '../../hooks/useCurrentDraftGroupId';
import { TranslationGroupProofreadFormData } from '../../state/types';
import { getFlag } from '../../utils/ui';

const StyledSelectInput = styled(FormControl)`
    & .MuiInputBase-root {
        height: 3.2rem;
        width: fit-content;
        border-radius: 8px;
        text-transform: capitalize;
        font-size: 1.4rem;
        font-weight: 500;
        background-color: #fff;

        &:hover {
            background-color: transparent;
        }

        * {
            cursor: pointer;
        }
    }

    & .MuiIconButton-root {
        transform: scale(0.66);
        translate: 4px;
    }

    & fieldset.MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.colors.black[300]} !important;
        border-width: 1px !important;
    }

    & .Mui-focused {
        & fieldset.MuiOutlinedInput-notchedOutline {
            border-color: ${({ theme }) => theme.colors.black[300]} !important;
            border-width: 1px !important;
        }
    }
`;

const StyledSelect = styled(Select)`
    & .MuiSelect-select {
        display: flex;
        align-items: center;
        padding-right: 8px !important;
    }
`;

const DownIcon = styled(MaskedIcon)`
    translate: -6px;
`;

const paperSx = (theme: Theme) => ({
    width: '20rem',
    borderRadius: '8px',
    position: 'relative',
    top: '8px',
    padding: '8px',
    boxShadow: '1.1px 2.7px 3.8px -1.2px #BBBBBB42, 0.5px 1.3px 1.8px -0.6px #BBBBBB2E, 0.3px 0.8px 1.1px 0px #BBBBBB1C',
    '& .MuiList-root': {
        padding: 0,
    },
    '& .Mui-selected': {
        backgroundColor: `${theme.colors.black[100]} !important`,
    },
    '& .MuiMenuItem-root': {
        borderRadius: '8px',
        height: '4rem',
        textTransform: 'capitalize',
        fontSize: '1.4rem',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: `${theme.colors.black[100]} !important`,
        },
    },
});

const Img = styled.img`
    width: 18px;
    height: 14px;
    margin-right: 6px;
`;

function LanguageSelect() {
    const groupId = useCurrentDraftGroupId();
    const { data: group } = useGetTranslationGroup(groupId!);
    const { watch, setValue } = useFormContext<TranslationGroupProofreadFormData>();
    const theme = useTheme();

    const translations = group?.translations;
    const selectedTranslationId = watch('selectedTranslationId');

    const handleChange = async (event: SelectChangeEvent<unknown>) => {
        setValue('selectedTranslationId', event.target.value as string);
    };

    if (!translations || !selectedTranslationId) {
        return null;
    }

    return (
        <StyledSelectInput>
            <StyledSelect
                MenuProps={{
                    PaperProps: { sx: paperSx(theme) },
                    transformOrigin: { vertical: -8, horizontal: 'left' },
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                }}
                id="proofread-select"
                value={selectedTranslationId}
                onChange={handleChange}
                IconComponent={() => <DownIcon src={arrowDown} size="16px" />}>
                {translations.map(({ id, language, locale }) => {
                    return (
                        <MenuItem value={id} key={id}>
                            {getFlag(Img, locale)}
                            {language}
                        </MenuItem>
                    );
                })}
            </StyledSelect>
        </StyledSelectInput>
    );
}

export default LanguageSelect;
